import {
  channels,
  isAnswerable,
  isVisibleQnaStatus,
  participantStateSchema,
  podiumRevealMs,
  qnaListResultSchema,
  type Answer,
  type PublicSlide,
  type QnaQuestion,
  type ServerEvent,
  REACTION_EMOJI,
  REACTIONS,
  type Reaction,
} from "@engage/contracts";
import { connectRealtime, RealtimeRpcError, type RealtimeConnection, type RealtimeStatus } from "@engage/realtime/client";
import { useEffect, useReducer, useRef, useState } from "preact/hooks";
import type { Joined } from "./api";
import { AnswerForm, Countdown, promptClass } from "./answer";
import type { RuntimeConfig } from "./config";
import { useI18n } from "./i18n";
import { QnaView } from "./qna";
import { Results } from "./results";
import { forget } from "./storage";

/**
 * A participant in a live session.
 *
 * Events keep the screen current; `session.state` puts it right after every connect.
 * The two can disagree for an instant, and the state RPC always wins — it is what the
 * server believes now, while an event is what it believed a moment ago.
 */

export interface Round {
  roundId: string;
  slideId: string;
  /**
   * When answers open, on this phone's clock: a quiz question is read for a moment
   * first, and its tiles appear only then.
   */
  opensAt: number;
  /** On this phone's clock, corrected by the server's (see `offset`). */
  deadline: number | null;
  closed: boolean;
  answered: boolean;
  results: { tallies: Record<string, number>; correctOptionIds?: string[]; acceptedAnswers?: string[] } | null;
  /** What this phone sent, to say "correct" or not once results are revealed. */
  mine: Answer | null;
}

interface State {
  slide: PublicSlide | null;
  round: Round | null;
  score: number;
  rank: number | null;
  /** Points this participant earned in the last closed round. */
  lastAwarded: number | null;
  /** The hall of fame is on screen: show this person where they finished. */
  podium: boolean;
  /**
   * When this phone heard the hall of fame begin, to hold its owner's place back until
   * the stage reveals it (contracts PODIUM). Null when it was already on at connect —
   * the reveal is under way or over, and the place can show at once.
   */
  podiumAt: number | null;
  ended: boolean;
  /** The presenter took this person out of the session. */
  removed: boolean;
  qna: { enabled: boolean; questions: QnaQuestion[]; myVotes: string[] };
}

type Action =
  | { kind: "event"; event: ServerEvent; offset: number }
  | { kind: "sync"; state: ReturnType<typeof participantStateSchema.parse>; offset: number }
  | { kind: "qna"; qna: State["qna"] }
  | { kind: "answered"; mine: Answer }
  | { kind: "closed" }
  | { kind: "voted"; question: QnaQuestion };

const deadlineOf = (openedAt: string, timeLimitMs: number, offset: number) =>
  timeLimitMs > 0 ? Date.parse(openedAt) + timeLimitMs - offset : null;

function upsert(questions: QnaQuestion[], q: QnaQuestion): QnaQuestion[] {
  const rest = questions.filter((x) => x.questionId !== q.questionId);
  return isVisibleQnaStatus(q.status) ? [...rest, q] : rest;
}

function reducer(s: State, a: Action): State {
  switch (a.kind) {
    case "sync": {
      const r = a.state.round;
      return {
        ...s,
        slide: a.state.slide,
        score: a.state.score,
        rank: a.state.rank,
        podium: a.state.podium,
        podiumAt: a.state.podium ? s.podiumAt : null,
        round: r && {
          roundId: r.roundId,
          slideId: r.slideId,
          opensAt: Date.parse(r.openedAt) - a.offset,
          deadline: deadlineOf(r.openedAt, r.timeLimitMs, a.offset),
          closed: r.closed,
          answered: r.answered,
          results: r.results,
          // Survives a sync of the same round; a new round starts with nothing sent.
          mine: s.round?.roundId === r.roundId ? s.round.mine : null,
        },
      };
    }
    case "qna":
      return { ...s, qna: a.qna };
    case "answered":
      return s.round ? { ...s, round: { ...s.round, answered: true, mine: a.mine } } : s;
    case "closed":
      return s.round ? { ...s, round: { ...s.round, closed: true } } : s;
    case "voted":
      return {
        ...s,
        qna: {
          ...s.qna,
          questions: upsert(s.qna.questions, a.question),
          myVotes: [...s.qna.myVotes, a.question.questionId],
        },
      };
    case "event": {
      const e = a.event;
      switch (e.type) {
        case "slide.changed":
          return {
            ...s,
            slide: e.slide,
            round: s.round?.slideId === e.slideId ? s.round : null,
            lastAwarded: null,
          };
        case "round.opened":
          if (s.round?.roundId === e.roundId) return s;
          return {
            ...s,
            round: {
              roundId: e.roundId,
              slideId: e.slideId,
              opensAt: Date.parse(e.openedAt) - a.offset,
              deadline: deadlineOf(e.openedAt, e.timeLimitMs, a.offset),
              closed: false,
              answered: false,
              results: null,
              mine: null,
            },
            lastAwarded: null,
          };
        case "round.closed":
          return s.round?.roundId === e.roundId ? { ...s, round: { ...s.round, closed: true } } : s;
        case "results.revealed":
          return s.round?.roundId === e.roundId
            ? {
                ...s,
                round: {
                  ...s.round,
                  closed: true,
                  results: {
                    tallies: e.tallies,
                    ...(e.correctOptionIds ? { correctOptionIds: e.correctOptionIds } : {}),
                    ...(e.acceptedAnswers ? { acceptedAnswers: e.acceptedAnswers } : {}),
                  },
                },
              }
            : s;
        case "score.updated":
          return { ...s, score: e.score, rank: e.rank, lastAwarded: e.lastAwarded };
        case "session.ended":
          return { ...s, ended: true };
        case "participant.removed":
          return { ...s, removed: true };
        case "stage.changed": {
          const podium = e.stage.phase === "podium";
          return { ...s, podium, podiumAt: podium ? (s.podium ? s.podiumAt : Date.now()) : null };
        }
        case "qna.question":
          return { ...s, qna: { ...s.qna, questions: upsert(s.qna.questions, e.question) } };
        case "qna.updated": {
          const current = s.qna.questions.find((q) => q.questionId === e.questionId);
          if (!current) return s;
          return {
            ...s,
            qna: { ...s.qna, questions: upsert(s.qna.questions, { ...current, status: e.status, upvoteCount: e.upvoteCount }) },
          };
        }
        default:
          return s;
      }
    }
  }
}

const initialState: State = {
  slide: null,
  round: null,
  score: 0,
  rank: null,
  lastAwarded: null,
  podium: false,
  podiumAt: null,
  ended: false,
  removed: false,
  qna: { enabled: false, questions: [], myVotes: [] },
};

export function Live({ config, joined, onLeave }: { config: RuntimeConfig; joined: Joined; onLeave: () => void }) {
  const { t } = useI18n();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [status, setStatus] = useState<RealtimeStatus | "refused">("connecting");
  const [view, setView] = useState<"session" | "qna">("session");
  const connection = useRef<RealtimeConnection | null>(null);
  const offset = useRef(0);

  useEffect(() => {
    const sessionId = joined.session.sessionId;

    const resync = async (conn: RealtimeConnection) => {
      try {
        const sent = Date.now();
        const s = participantStateSchema.parse(await conn.rpc("session.state", {}));
        // Server clock minus ours, taking the midpoint of the round trip. Only the
        // countdown uses it; the server decides what is late.
        offset.current = Date.parse(s.serverTime) - (sent + Date.now()) / 2;
        dispatch({ kind: "sync", state: s, offset: offset.current });
        const q = qnaListResultSchema.parse(await conn.rpc("qna.list", {}));
        dispatch({ kind: "qna", qna: q });
      } catch {
        /* the next event, or the next reconnect, will bring us up to date */
      }
    };

    const conn = connectRealtime({
      url: config.realtimeWsUrl,
      getConnectData: async () => ({ joinCode: joined.code, rejoinToken: joined.rejoinToken }),
      channels: [
        channels.session(sessionId),
        channels.personal({ sessionId, participantId: joined.participantId }),
      ],
      onEvent: (event) => dispatch({ kind: "event", event, offset: offset.current }),
      onStatus: (s) => {
        setStatus(s);
        if (s === "connected") void resync(conn);
      },
      // The rejoin token was refused: the session has ended or no longer exists. There
      // is nothing to reconnect to, and this device should stop trying.
      onRefused: () => {
        forget(joined.code);
        setStatus("refused");
      },
    });
    connection.current = conn;
    return () => conn.close();
  }, [config.realtimeWsUrl, joined]);

  useEffect(() => {
    if (state.ended || state.removed) forget(joined.code);
    // Nothing more will come that this phone may act on.
    if (state.removed) connection.current?.close();
  }, [state.ended, state.removed, joined.code]);

  if (status === "refused" || state.ended || state.removed) {
    return (
      <section class="card stack center final-card">
        <h1>{state.removed ? t("ended.removed") : state.ended ? t("ended.title") : t("ended.gone")}</h1>
        {state.ended && <FinalPlace score={state.score} rank={state.rank} claimCode={joined.claimCode ?? null} />}
        <button class="primary" onClick={onLeave}>
          {t("ended.again")}
        </button>
      </section>
    );
  }

  const submit = async (answer: Answer): Promise<string | null> => {
    if (!connection.current || !state.round) return t("join.error");
    try {
      await connection.current.rpc("answer.submit", { roundId: state.round.roundId, answer });
      dispatch({ kind: "answered", mine: answer });
      return null;
    } catch (err) {
      const code = err instanceof RealtimeRpcError ? err.code : 0;
      if (code === 4409) {
        dispatch({ kind: "answered", mine: answer });
        return null;
      }
      if (code === 4410) {
        dispatch({ kind: "closed" });
        return null;
      }
      if (code === 4422 && err instanceof Error) return t("live.invalid", { reason: err.message });
      return t("join.error");
    }
  };

  return (
    <>
      <header class="bar">
        <span class="who">{joined.displayName ?? ""}</span>
        <span class="bar-end">
          {status !== "connected" && (
            <span class="status" role="status">
              {status === "connecting" ? t("live.connecting") : t("live.reconnecting")}
            </span>
          )}
          {state.score > 0 && (
            <span class="pill">
              {t("live.score", { score: state.score })}
              {/* Not during the hall of fame: the rank would give away the reveal. */}
              {state.rank && !state.podium ? ` · ${t("live.rank", { rank: state.rank })}` : ""}
            </span>
          )}
          {state.qna.enabled && (
            <button class="tab" onClick={() => setView(view === "qna" ? "session" : "qna")} aria-pressed={view === "qna"}>
              {view === "qna" ? t("qna.back") : t("qna.tab")}
            </button>
          )}
        </span>
      </header>

      {view === "qna" && connection.current ? (
        <QnaView
          connection={connection.current}
          qna={state.qna}
          onVoted={(q) => dispatch({ kind: "voted", question: q })}
        />
      ) : state.podium ? (
        <PodiumReveal podiumAt={state.podiumAt} score={state.score} rank={state.rank} claimCode={joined.claimCode ?? null} />
      ) : (
        // Keyed by slide: a new question is a new screen, and arrives as one.
        <SlideView key={state.slide?.id ?? "lobby"} state={state} name={joined.displayName} submit={submit} />
      )}

      {/* Not while a question waits for an answer: the tiles sit under the thumb too. */}
      {view === "session" && connection.current && !answering(state) && <ReactionBar connection={connection.current} />}
    </>
  );
}

/** Is there a question on screen this person has yet to answer? */
function answering({ slide, round }: State): boolean {
  return slide !== null && isAnswerable(slide.type) && round !== null && !round.closed && !round.answered;
}

/** How long the buttons rest after a tap: the server takes one a phone per 700 ms anyway. */
const REACT_REST_MS = 700;

/** Six taps for the big screen. The phone shows its own tap at once; the screen gets them in batches. */
function ReactionBar({ connection }: { connection: RealtimeConnection }) {
  const { t } = useI18n();
  const [resting, setResting] = useState(false);
  const [popped, setPopped] = useState<Reaction | null>(null);

  const send = (reaction: Reaction) => {
    if (resting) return;
    setResting(true);
    setPopped(reaction);
    setTimeout(() => {
      setResting(false);
      setPopped(null);
    }, REACT_REST_MS);
    void connection.rpc("reaction.send", { reaction }).catch(() => {});
  };

  return (
    <nav class="reactions" aria-label={t("live.react")}>
      {REACTIONS.map((r) => (
        <button
          key={r}
          type="button"
          class={popped === r ? "popped" : undefined}
          aria-label={t(`live.reaction.${r}`)}
          disabled={resting && popped !== r}
          onClick={() => send(r)}
        >
          {REACTION_EMOJI[r]}
        </button>
      ))}
    </nav>
  );
}

function SlideView({
  state,
  name,
  submit,
}: {
  state: State;
  name: string | null;
  submit: (answer: Answer) => Promise<string | null>;
}) {
  const { t } = useI18n();
  const { slide, round } = state;

  if (!slide) {
    return (
      <section class="card stack center lobby">
        <div class="pulse" aria-hidden="true">
          <span />
        </div>
        <h1>{name ? t("live.lobby.named", { name }) : t("live.lobby.title")}</h1>
        <p class="muted">{t("live.lobby.body")}</p>
      </section>
    );
  }

  const answerable = isAnswerable(slide.type);
  // Until its answers open, a quiz question is only read: no tiles, no clock.
  const readingFor = useUntil(round && !round.closed ? round.opensAt : null);
  const open = answerable && round !== null && !round.closed && readingFor === 0;

  // A slide with nothing to answer is only its words.
  if (!answerable) {
    return (
      <section class="card stack center slide-only">
        <h1 class={promptClass(slide.prompt)}>{slide.prompt}</h1>
      </section>
    );
  }

  return (
    // Fills the screen below the bar, so answer tiles can sit low, under the thumb.
    <section class="stack asking">
      <div class="prompt-head">
        <h1 class={promptClass(slide.prompt)}>{slide.prompt}</h1>
        {open && <Countdown deadline={round.deadline} limitMs={slide.timeLimitMs} />}
      </div>

      {(!round || (!round.closed && readingFor > 0)) && (
        <div class="card notice" data-reading={readingFor > 0 || undefined}>
          <div class="dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <p class="muted">{readingFor > 0 ? t("live.answersIn", { seconds: readingFor }) : t("live.getReady")}</p>
        </div>
      )}

      {open && !round.answered && <AnswerForm slide={slide} submit={submit} />}

      {open && round.answered && (
        <div class="card notice">
          <span class="tick" aria-hidden="true">
            ✓
          </span>
          <strong>{t("live.sent")}</strong>
          <p class="muted">{t("live.sentWait")}</p>
        </div>
      )}

      {round && round.closed && !round.results && (
        <div class="card notice">
          <strong>{t("live.closed")}</strong>
          <p class="muted">{round.answered ? t("live.closedBody") : t("live.missed")}</p>
        </div>
      )}

      {round?.results && <Results slide={slide} round={round} lastAwarded={state.lastAwarded} />}
    </section>
  );
}

/** Whole seconds until `at` on this phone's clock, 0 once it has passed; re-renders until then. */
function useUntil(at: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (at === null || at <= Date.now()) return;
    const id = setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= at) clearInterval(id);
    }, 250);
    return () => clearInterval(id);
  }, [at]);
  return at === null ? 0 : Math.max(0, Math.ceil((at - Math.max(now, Date.now())) / 1000));
}

/** Gold, silver, bronze — drawn in CSS, as a phone may lack the emoji. */
const MEDALS = ["gold", "silver", "bronze"];

/**
 * The hall of fame, from the phone: a drumroll while the screen calls the places, then
 * this person's own — timed to the moment the stage reveals it, so the winner finds out
 * with the room rather than a few seconds early from their pocket.
 */
function PodiumReveal({
  podiumAt,
  score,
  rank,
  claimCode,
}: {
  podiumAt: number | null;
  score: number;
  rank: number | null;
  claimCode: string | null;
}) {
  const { t } = useI18n();
  const placed = rank !== null && score > 0;
  const [dueAt] = useState(() => (podiumAt === null ? 0 : podiumAt + podiumRevealMs(placed ? rank : null)));
  const [shown, setShown] = useState(() => Date.now() >= dueAt);
  useEffect(() => {
    if (shown) return;
    const id = setTimeout(() => setShown(true), dueAt - Date.now());
    return () => clearTimeout(id);
  }, [shown, dueAt]);

  if (!shown) {
    return (
      <section class="card stack center final-card">
        <h1>{t("final.title")}</h1>
        <div class="drumroll" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <p class="big">{t("final.watch")}</p>
        <p class="muted">{t("final.waiting")}</p>
      </section>
    );
  }

  const podium = placed && rank <= 3;
  return (
    <section class={`card stack center final-card revealed${podium ? " on-podium" : ""}`} aria-live="polite">
      {podium && <PhoneConfetti />}
      <h1>{placed && rank === 1 ? t("final.winner") : t("final.title")}</h1>
      <FinalPlace score={score} rank={rank} claimCode={claimCode} />
    </section>
  );
}

/** A handful of paper pieces falling through the card, for a place on the podium. */
function PhoneConfetti() {
  return (
    <div class="phone-confetti" aria-hidden="true">
      {Array.from({ length: 18 }, (_, i) => (
        <span
          key={i}
          style={{
            left: `${(i * 37) % 100}%`,
            animationDelay: `${(i % 6) * 120}ms`,
            animationDuration: `${1.8 + (i % 5) * 0.25}s`,
            background: ["#e11d48", "#2563eb", "#d97706", "#059669", "#fde047"][i % 5],
          }}
        />
      ))}
    </div>
  );
}

/** Where this person finished — their own line of the hall of fame. */
function FinalPlace({ score, rank, claimCode }: { score: number; rank: number | null; claimCode: string | null }) {
  const { t, ordinal } = useI18n();
  // No rank means no answer was ever scored: nothing to place.
  if (rank === null || score === 0) return <p>{t("final.noScore")}</p>;
  const medal = MEDALS[rank - 1];
  return (
    <div class="final">
      {medal && (
        <span class={`medal ${medal}`} aria-hidden="true">
          {rank}
        </span>
      )}
      <p class="big">{ordinal("final.place", rank)}</p>
      <p>{t("final.points", { score })}</p>
      {medal && <p class="success">{t("final.podium")}</p>}
      {/* What proves a prize is theirs: the presenter's results show the same code. */}
      {claimCode && (
        <div class="claim">
          <span>{t("final.claim")}</span>
          <strong class="claim-code" data-claim-code>
            {claimCode}
          </strong>
          <small>{t("final.claim.hint")}</small>
        </div>
      )}
    </div>
  );
}
